import type { Appearance } from '@stripe/stripe-js';

/**
 * Stripe Elements renders inside a sandboxed iframe, so it can't inherit the
 * app's CSS custom properties directly — they have to be read from the
 * document and handed to Stripe's own Appearance API instead. Reading them at
 * call time (rather than hardcoding hex values) means this stays correct if
 * the tokens change, and follows whichever theme (light/dark) is active.
 *
 * Several of our tokens (--app-card, --app-fg, --app-muted, --app-border)
 * are indirections onto another custom property (e.g. `--app-card:
 * var(--surface)`). getComputedStyle().getPropertyValue() on a custom
 * property returns that literal unresolved string, not the final color —
 * custom properties only get substituted when used by a real CSS property.
 * Passing "var(--surface)" straight to Stripe as `colorBackground` is
 * invalid, so Stripe silently ignored it and fell back to its own
 * system-theme detection, independent of the app's light/dark toggle. Applying
 * the token to an off-screen element's actual `color` and reading that back
 * forces the browser to fully resolve it.
 */
function resolveColor(varName: string, fallback: string): string {
  const probe = document.createElement('div');
  probe.style.color = `var(${varName})`;
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  document.body.removeChild(probe);
  return resolved && resolved !== '' ? resolved : fallback;
}

export function stripeAppearance(): Appearance {
  const root = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => resolveColor(name, root.getPropertyValue(name).trim() || fallback);
  const rgb = (name: string, fallback: string) => {
    const raw = root.getPropertyValue(name).trim();
    return raw ? `rgb(${raw})` : fallback;
  };
  const raw = (name: string, fallback: string) => root.getPropertyValue(name).trim() || fallback;

  return {
    theme: document.documentElement.classList.contains('dark') ? 'night' : 'stripe',
    variables: {
      colorPrimary: rgb('--gold', '#FF993B'),
      colorBackground: v('--app-card', '#FBF5E4'),
      colorText: v('--app-fg', '#1C1B19'),
      colorTextSecondary: v('--app-muted', '#6B6248'),
      colorDanger: rgb('--crimson', '#A61029'),
      fontFamily: raw('--font-sans', 'system-ui, sans-serif'),
      borderRadius: raw('--r-field', '8px'),
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        border: `1px solid ${v('--app-border', 'rgba(0,0,0,0.14)')}`,
        boxShadow: 'none',
      },
      '.Input:focus': {
        border: `1px solid ${rgb('--gold', '#FF993B')}`,
        boxShadow: 'none',
      },
      '.Label': {
        color: v('--app-muted', '#6B6248'),
        fontSize: '13px',
      },
    },
  };
}
